import { CircleHelp, Loader } from "lucide-react";
import { FC, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useUpdateSKUMutation } from "@/data/mutations";
import { SKU } from "@/data/types";
import { SKUFormValues } from "@/lib/forms";
import { canEditSKU } from "@/lib/permissions";
import useStore from "@/state";

import { FileUploadInput } from "./file-upload-input";
import { ProductPreview } from "./product-preview";

type Props = {
  sku: SKU;
};

export const ProductTab: FC<Props> = ({ sku }) => {
  const form = useFormContext<SKUFormValues>();

  const { session } = useStore();

  const updateSKUMutation = useUpdateSKUMutation(sku.id);

  const onSubmit = async (values: SKUFormValues) => {
    await updateSKUMutation.mutateAsync({
      name: values.name,
      image_url: values.image_url,
      animation_url: values.animation_url,
      description: values.description,
    });

    form.reset({}, { keepValues: true });
  };

  const lacksPermission = useMemo(() => {
    return !canEditSKU(session?.permissions);
  }, [session]);

  const isSubmitDisabled = useMemo(() => {
    return (
      lacksPermission ||
      !form.formState.isDirty ||
      !form.formState.isValid ||
      updateSKUMutation.isPending
    );
  }, [
    lacksPermission,
    form.formState.isValid,
    form.formState.isDirty,
    updateSKUMutation.isPending,
  ]);

  return (
    <div className="flex flex-col lg:flex-row gap-6 mt-6">
      <div className="flex-none lg:flex-1 lg:min-w-[320px]">
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <FormField
            control={form.control}
            name="name"
            disabled={lacksPermission}
            render={({ field }) => (
              <FormItem className="mt-6">
                <FormLabel>Product Name*</FormLabel>
                <FormControl>
                  <Input placeholder="" {...field} />
                </FormControl>
                <FormDescription>
                  This is the display name of the SKU and its associated Digital
                  Product. Max 75 characters.
                </FormDescription>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="image_url"
            disabled={lacksPermission}
            render={({ field }) => (
              <FormItem className="mt-6">
                <FormLabel>Image</FormLabel>
                <FormControl>
                  <FileUploadInput {...field} fileType="image" />
                </FormControl>
                <FormDescription>
                  Image for product in PNG, JPEG, WebP, GIF, or SVG - 1:1 ratio
                  recommended. Max 10,000 pixels and 10MB.
                </FormDescription>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="animation_url"
            disabled={lacksPermission}
            render={({ field }) => (
              <FormItem className="mt-6">
                <FormLabel>Video</FormLabel>
                <FormControl>
                  <FileUploadInput fileType="video" {...field} />
                </FormControl>
                <FormDescription>
                  Animation for product in MP4, MOV, AVI, WebM, or MPG - 1:1
                  ratio recommended. Max 200MB.
                </FormDescription>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            disabled={lacksPermission}
            render={({ field }) => (
              <FormItem className="mt-6">
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Experience our innovative spring collection, featuring sustainably sourced materials, and contemporary designs. Each piece has a refined blend of cotton and polyester to create a balance of breathability and durability."
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  The description can be viewed by end-consumers. Markdown
                  formatting is supported. Max 2-3 sentences.
                </FormDescription>
              </FormItem>
            )}
          />

          <div className="mt-6 flex gap-x-2">
            <CircleHelp className="text-muted-foreground" />
            <p className="text-sm text-muted-foreground font-medium">
              View our{" "}
              <Link
                className="underline text-black"
                to="https://docs.legitimate.tech/public-docs/project-docs/required-activation-assets/image-specs"
                target="_blank"
              >
                documentation
              </Link>{" "}
              on required assets for Digital Products for in-depth requirements
              for image and video assets.
            </p>
          </div>

          <Button className="mt-6" type="submit" disabled={isSubmitDisabled}>
            {updateSKUMutation.isPending ? (
              <Loader className="animate-spin" />
            ) : null}
            Save
          </Button>
        </form>
      </div>
      <ProductPreview sku={sku} />
    </div>
  );
};
